/**
 * Redirect Persistence Service
 *
 * Manages storing and retrieving redirect URLs across the authentication flow.
 * Used for deep linking to encounters - allows users to click a direct link,
 * authenticate, and then be redirected to the intended destination.
 *
 * Uses localStorage for persistence (survives page reloads and tab close).
 */

const REDIRECT_KEY = 'auth_redirect_url';
// Last user who signed in on this browser. Unlike the 'user' key, session cleanup
// (logout, failed token refresh) never removes it, so a saved link keeps its owner.
const LAST_USER_KEY = 'auth_last_user_id';
const MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes expiry

interface StoredRedirect {
  url: string;
  timestamp: number;
  // Last user signed in on this browser when the link was saved; null if nobody ever was
  userId?: string | null;
}

export const redirectPersistenceService = {
  /**
   * Record who just signed in on this browser
   */
  rememberSignedInUser(userId: string): void {
    localStorage.setItem(LAST_USER_KEY, userId);
  },

  getLastSignedInUserId(): string | null {
    return localStorage.getItem(LAST_USER_KEY);
  },

  /**
   * Store a URL to redirect to after authentication
   */
  setRedirectUrl(url: string, previousUserId: string | null = null): void {
    // Skip storing login-related paths
    if (url.includes('/login') || url.includes('/mobile-login')) {
      return;
    }

    const data: StoredRedirect = {
      url,
      timestamp: Date.now(),
      userId: previousUserId,
    };
    localStorage.setItem(REDIRECT_KEY, JSON.stringify(data));
  },

  /**
   * Get and clear the stored redirect URL
   * Returns null if expired, not set, or saved from another user's session
   */
  getAndClearRedirectUrl(userId: string | undefined): string | null {
    const stored = localStorage.getItem(REDIRECT_KEY);
    if (!stored) return null;

    localStorage.removeItem(REDIRECT_KEY);

    try {
      const data: StoredRedirect = JSON.parse(stored);
      // Check expiry
      if (Date.now() - data.timestamp > MAX_AGE_MS) {
        return null;
      }
      // Never send the next person on this browser to the previous user's page (AMP-131)
      if (data.userId && data.userId !== userId) {
        return null;
      }
      return data.url;
    } catch {
      return null;
    }
  },

  /**
   * Clear any stored redirect URL without returning it
   */
  clearRedirectUrl(): void {
    localStorage.removeItem(REDIRECT_KEY);
  },
};
